/**
 * Loose ore chunks and the collector cone (M2 plan §6 M2-16, spec §5.3). Chunks break off rocks (chip) or scatter
 * from a burst, drift in float64 world space with no collider, and despawn `chip.chunkLifeSeconds` of game time
 * after they spawn. In Mining mode the collector cone (`collector.range`, `halfAngle` ahead of the ship's nose)
 * pulls chunks toward the ship; at `captureDistance` a chunk goes into the hold when the mirror has room (the
 * mirror's `cargoUsedT` rises at once and `oreCollected` fires), else it is rejected: it drifts on, and `holdFull`
 * fires once per rejection streak. Everything here is allocation-free (D-014). M10 P5a6a: a chunk takes
 * `tonnes ÷ mirror.holdMult[ware]` of the hold (the flown hull's class multiplier; 1 today, so today's numbers).
 */
import { SIM_DT } from '../core/loop'
import { TETHERSTONE_INDEX, type MiningConfig } from '../data/m2Schemas'
import { Life, Pos64, Rot, Vel } from '../world/components'
import { TOOL, type CareerMirror, type ChunkPool } from './contracts'
import { ToolControl } from './components'
import { emitSpaceEvent, HOLD_FULL_SOURCE, SEV, type SpaceEventRing } from './events'

/** The chunk pool plus the collector's runtime state. */
export interface ChunkState {
  readonly pool: ChunkPool
  /** 1 = reached the capture point with no room in the hold: not pulled until the hold has room for it. */
  readonly rejected: Uint8Array
  /** `holdFull` fired for the current rejection streak. */
  holdFullLatched: boolean
  /** Free tonnes when the latch was set: more room than this ends the streak. */
  latchFreeT: number
}

export function createChunkState(pool: ChunkPool): ChunkState {
  return { pool, rejected: new Uint8Array(pool.capacity), holdFullLatched: false, latchFreeT: 0 }
}

/**
 * Spawns a chunk at a float64 world point with a velocity; returns its slot, or -1 when the pool is full (the ore
 * is lost, like a chunk that despawns). `born` is game time, `seed` the visual seed in [0, 1).
 */
export function spawnChunk(
  st: ChunkState,
  x: number,
  y: number,
  z: number,
  vx: number,
  vy: number,
  vz: number,
  ware: number,
  tonnes: number,
  born: number,
  seed: number,
): number {
  const pool = st.pool
  let slot = -1
  for (let i = 0; i < pool.high; i++) {
    if (pool.alive[i] === 0) {
      slot = i
      break
    }
  }
  if (slot < 0) {
    if (pool.high >= pool.capacity) return -1
    slot = pool.high
    pool.high++
  }
  pool.alive[slot] = 1
  pool.live++
  pool.x[slot] = x
  pool.y[slot] = y
  pool.z[slot] = z
  pool.vx[slot] = vx
  pool.vy[slot] = vy
  pool.vz[slot] = vz
  pool.ware[slot] = ware
  pool.tonnes[slot] = tonnes
  pool.born[slot] = born
  pool.seed[slot] = seed
  st.rejected[slot] = 0
  return slot
}

/** Removes chunk `slot` (captured or expired). */
export function killChunk(st: ChunkState, slot: number): void {
  const pool = st.pool
  if (pool.alive[slot] === 0) return
  pool.alive[slot] = 0
  pool.live--
  st.rejected[slot] = 0
  while (pool.high > 0 && pool.alive[pool.high - 1] === 0) pool.high--
}

/** Removes every chunk (new career, load). */
export function clearChunks(st: ChunkState): void {
  const pool = st.pool
  for (let i = 0; i < pool.capacity; i++) {
    pool.alive[i] = 0
    st.rejected[i] = 0
  }
  pool.high = 0
  pool.live = 0
  st.holdFullLatched = false
  st.latchFreeT = 0
}

/**
 * One physics-stage tick of every chunk: expiry, the collector cone and capture for the ship `eid` (its
 * `ToolControl.mode` must be Mining; -1 or a dead ship collects nothing), then drift. `now` is game time.
 */
export function stepChunks(
  st: ChunkState,
  eid: number,
  mirror: CareerMirror,
  events: SpaceEventRing,
  cfg: Pick<MiningConfig, 'chip' | 'collector'>,
  now: number,
): void {
  const pool = st.pool
  const dt = SIM_DT
  const col = cfg.collector
  const life = cfg.chip.chunkLifeSeconds
  const collecting = eid >= 0 && Life.alive[eid] === 1 && ToolControl.mode[eid] === TOOL.mining
  let sx = 0
  let sy = 0
  let sz = 0
  let svx = 0
  let svy = 0
  let svz = 0
  let fx = 0
  let fy = 0
  let fz = -1
  if (collecting) {
    sx = Pos64.x[eid]
    sy = Pos64.y[eid]
    sz = Pos64.z[eid]
    svx = Vel.x[eid]
    svy = Vel.y[eid]
    svz = Vel.z[eid]
    // Nose = local −Z rotated into world (q · (0, 0, −1)).
    const qx = Rot.x[eid]
    const qy = Rot.y[eid]
    const qz = Rot.z[eid]
    const qw = Rot.w[eid]
    fx = -2 * (qx * qz + qw * qy)
    fy = -2 * (qy * qz - qw * qx)
    fz = -(1 - 2 * (qx * qx + qy * qy))
  }
  const cosCone = Math.cos(col.halfAngle)
  const range2 = col.range * col.range
  const capture2 = col.captureDistance * col.captureDistance
  const freeT = mirror.cargoCapacityT - mirror.cargoUsedT
  if (st.holdFullLatched && freeT > st.latchFreeT + 1e-6) st.holdFullLatched = false
  for (let i = 0; i < pool.high; i++) {
    if (pool.alive[i] === 0) continue
    if (now - pool.born[i] >= life) {
      killChunk(st, i)
      continue
    }
    if (collecting) {
      const rx = pool.x[i] - sx
      const ry = pool.y[i] - sy
      const rz = pool.z[i] - sz
      const d2 = rx * rx + ry * ry + rz * rz
      const ware = pool.ware[i]
      const holdT = pool.tonnes[i] / (mirror.holdMult?.[ware] ?? 1)
      if (st.rejected[i] === 1 && mirror.cargoCapacityT - mirror.cargoUsedT >= holdT) st.rejected[i] = 0
      if (d2 <= capture2 && st.rejected[i] === 0) {
        const tonnes = pool.tonnes[i]
        if (mirror.cargoCapacityT - mirror.cargoUsedT >= holdT) {
          mirror.cargoUsedT += holdT
          emitSpaceEvent(events, SEV.oreCollected, eid, ware, 0, 0, pool.x[i], pool.y[i], pool.z[i], tonnes, 0)
          if (ware === TETHERSTONE_INDEX) emitSpaceEvent(events, SEV.tetherstone, eid, 0, 0, 0, pool.x[i], pool.y[i], pool.z[i], tonnes, 0)
          st.holdFullLatched = false
          killChunk(st, i)
          continue
        }
        st.rejected[i] = 1
        if (!st.holdFullLatched) {
          st.holdFullLatched = true
          st.latchFreeT = mirror.cargoCapacityT - mirror.cargoUsedT
          emitSpaceEvent(events, SEV.holdFull, eid, HOLD_FULL_SOURCE.chunk, 0, 0, pool.x[i], pool.y[i], pool.z[i], tonnes, 0)
        }
      } else if (d2 <= range2 && d2 > 0 && st.rejected[i] === 0) {
        const d = Math.sqrt(d2)
        if ((rx * fx + ry * fy + rz * fz) / d >= cosCone) {
          // Steer the chunk's velocity toward (ship velocity + maxPullSpeed toward the ship) at pullAccel.
          const k = col.maxPullSpeed / d
          let ex = svx - rx * k - pool.vx[i]
          let ey = svy - ry * k - pool.vy[i]
          let ez = svz - rz * k - pool.vz[i]
          const e = Math.sqrt(ex * ex + ey * ey + ez * ez)
          const maxDv = col.pullAccel * dt
          if (e > maxDv) {
            const s = maxDv / e
            ex *= s
            ey *= s
            ez *= s
          }
          pool.vx[i] += ex
          pool.vy[i] += ey
          pool.vz[i] += ez
        }
      }
    }
    pool.x[i] += pool.vx[i] * dt
    pool.y[i] += pool.vy[i] * dt
    pool.z[i] += pool.vz[i] * dt
  }
}
